// src/components/dashboard/ClubOverviewStats.tsx
import React, { useState, useEffect } from "react";
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    Alert,
    Button
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import GroupsIcon from "@mui/icons-material/Groups";
import axiosInstance from "../../utils/axiosConfig";

interface ClubOverviewStatsProps {
    competitionId: string;
}

interface ClubOverviewRow {
    clubId: string;
    clubName: string;
    athleteCountElite: number;
    athleteCountBasis: number;
    athleteCountTotal: number;
    judgeCountElite: number;
    judgeCountBasis: number;
    judgeCountTotal: number;
}

const ClubOverviewStats: React.FC<ClubOverviewStatsProps> = ({ competitionId }) => {
    const [rows, setRows] = useState<ClubOverviewRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        axiosInstance
            .get<ClubOverviewRow[]>(`/api/competition-registrations/${competitionId}/club-overview`)
            .then(res => setRows(res.data))
            .catch(() => setError("Vereinsübersicht konnte nicht geladen werden."))
            .finally(() => setLoading(false));
    }, [competitionId]);

    const totals = rows.reduce(
        (acc, r) => ({
            athleteCountElite: acc.athleteCountElite + r.athleteCountElite,
            athleteCountBasis: acc.athleteCountBasis + r.athleteCountBasis,
            athleteCountTotal: acc.athleteCountTotal + r.athleteCountTotal,
            judgeCountElite: acc.judgeCountElite + r.judgeCountElite,
            judgeCountBasis: acc.judgeCountBasis + r.judgeCountBasis,
            judgeCountTotal: acc.judgeCountTotal + r.judgeCountTotal,
        }),
        { athleteCountElite: 0, athleteCountBasis: 0, athleteCountTotal: 0, judgeCountElite: 0, judgeCountBasis: 0, judgeCountTotal: 0 }
    );

    const handleCsvExport = () => {
        const header = "Verein;Sportler Elite;Sportler Basis;Sportler Gesamt;Kampfrichter Elite;Kampfrichter Basis;Kampfrichter Gesamt";
        const dataRows = rows.map(r =>
            [r.clubName, r.athleteCountElite, r.athleteCountBasis, r.athleteCountTotal, r.judgeCountElite, r.judgeCountBasis, r.judgeCountTotal].join(";")
        );
        const sumRow = [
            "Gesamt",
            totals.athleteCountElite,
            totals.athleteCountBasis,
            totals.athleteCountTotal,
            totals.judgeCountElite,
            totals.judgeCountBasis,
            totals.judgeCountTotal,
        ].join(";");

        const csv = [header, ...dataRows, sumRow].join("\r\n");
        const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `vereinsuebersicht_${competitionId}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Paper elevation={2} sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <GroupsIcon />
                    Vereinsübersicht
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleCsvExport}
                    disabled={rows.length === 0}
                >
                    CSV exportieren
                </Button>
            </Box>

            {rows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    Keine Meldungen vorhanden.
                </Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell rowSpan={2}><strong>Verein</strong></TableCell>
                                <TableCell align="center" colSpan={3} sx={{ borderBottom: 0 }}><strong>Sportler</strong></TableCell>
                                <TableCell align="center" colSpan={3} sx={{ borderBottom: 0 }}><strong>Kampfrichter</strong></TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell align="right">Elite</TableCell>
                                <TableCell align="right">Basis</TableCell>
                                <TableCell align="right">Gesamt</TableCell>
                                <TableCell align="right">Elite</TableCell>
                                <TableCell align="right">Basis</TableCell>
                                <TableCell align="right">Gesamt</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(row => (
                                <TableRow key={row.clubId} hover>
                                    <TableCell>{row.clubName}</TableCell>
                                    <TableCell align="right">{row.athleteCountElite}</TableCell>
                                    <TableCell align="right">{row.athleteCountBasis}</TableCell>
                                    <TableCell align="right">{row.athleteCountTotal}</TableCell>
                                    <TableCell align="right">{row.judgeCountElite}</TableCell>
                                    <TableCell align="right">{row.judgeCountBasis}</TableCell>
                                    <TableCell align="right">{row.judgeCountTotal}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow sx={{ backgroundColor: "action.hover" }}>
                                <TableCell><strong>Gesamt</strong></TableCell>
                                <TableCell align="right"><strong>{totals.athleteCountElite}</strong></TableCell>
                                <TableCell align="right"><strong>{totals.athleteCountBasis}</strong></TableCell>
                                <TableCell align="right"><strong>{totals.athleteCountTotal}</strong></TableCell>
                                <TableCell align="right"><strong>{totals.judgeCountElite}</strong></TableCell>
                                <TableCell align="right"><strong>{totals.judgeCountBasis}</strong></TableCell>
                                <TableCell align="right"><strong>{totals.judgeCountTotal}</strong></TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    );
};

export default ClubOverviewStats;
